import { prepareEventImageForUpload, DEFAULT_PREPARE_IMAGE_OPTIONS } from '../Utils/prepareEventImageForUpload'
import { measureImageDimensions } from '../Utils/measureImageDimensions'
import {
  DEFAULT_LIBRARY_ACCEPT,
  capturePoster,
  classifyLibraryFile,
  libraryDurationError,
  libraryVideoSizeError,
  probeVideoDuration,
  uploadLibraryClip,
  uploadPresignedAsset
} from './library/libraryMedia'

function parseFloatAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseFloat(val)
  return Number.isFinite(n) ? n : fallback
}

function parseIntAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseInt(val, 10)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Library picker for a photo or a video. Classifies the file, runs the
 * matching library pipeline, and publishes a stage action. The AppM owns
 * preview and confirm. This element does not open a camera or a capture clock.
 *
 *   <anear-media-upload
 *     purpose-photo="tribute_photo"
 *     purpose-video="tribute_video"
 *     poster-purpose="tribute_poster">
 *   </anear-media-upload>
 */
class AnearMediaUpload extends HTMLElement {
  constructor() {
    super()
    this._onUploadRequest = null
    this._onWorkflowStart = null
    this._publishAction = null
    this._eventId = null
    this._fileInput = null
    this._triggerBtn = null
    this._statusEl = null
    this._uploading = false
  }

  connectedCallback() {
    this.render()
  }

  initialize({ onUploadRequest, eventId, onWorkflowStart, publishAction } = {}) {
    this._onUploadRequest = onUploadRequest
    this._eventId = eventId
    this._onWorkflowStart = onWorkflowStart
    this._publishAction = publishAction
  }

  get label() {
    return this.getAttribute('label') || 'Choose a photo or video'
  }

  get uploadingLabel() {
    return this.getAttribute('uploading-label') || 'Uploading…'
  }

  get invalidTypeMessage() {
    return this.getAttribute('invalid-type-message') || 'Choose a photo or a video.'
  }

  get accept() {
    return this.getAttribute('accept') || DEFAULT_LIBRARY_ACCEPT
  }

  get actionName() {
    return this.getAttribute('action') || 'ANEAR_LIBRARY_MEDIA'
  }

  get purposePhoto() {
    return this.getAttribute('purpose-photo') || this.getAttribute('purpose') || 'tribute_photo'
  }

  get purposeVideo() {
    return this.getAttribute('purpose-video') || this.getAttribute('purpose') || 'tribute_video'
  }

  get posterPurpose() {
    return this.getAttribute('poster-purpose') || 'tribute_poster'
  }

  get optimizeImages() {
    const raw = this.getAttribute('optimize-images')
    if (raw === null) return true
    const lower = raw.toLowerCase()
    return lower !== 'false' && lower !== 'off' && lower !== '0'
  }

  get prepareImageOptions() {
    return {
      maxSourceMb: parseFloatAttr(this.getAttribute('max-source-mb'), DEFAULT_PREPARE_IMAGE_OPTIONS.maxSourceMb),
      maxEdgePx: parseIntAttr(this.getAttribute('max-edge-px'), DEFAULT_PREPARE_IMAGE_OPTIONS.maxEdgePx),
      passthroughMaxKb: parseIntAttr(this.getAttribute('passthrough-max-kb'), DEFAULT_PREPARE_IMAGE_OPTIONS.passthroughMaxKb),
      webpQuality: parseFloatAttr(this.getAttribute('webp-quality'), DEFAULT_PREPARE_IMAGE_OPTIONS.webpQuality),
      imageOptimization: this.optimizeImages
    }
  }

  get maxUploadMb() {
    return Math.max(1, parseIntAttr(this.getAttribute('max-upload-mb'), 250))
  }

  get minSeconds() {
    return Math.max(0, parseIntAttr(this.getAttribute('min-seconds'), 0))
  }

  get maxUploadSeconds() {
    return Math.max(0, parseIntAttr(this.getAttribute('max-upload-seconds'), 0))
  }

  get autoTranscode() {
    const raw = this.getAttribute('auto-transcode')
    return raw != null && raw !== 'false'
  }

  get transcodeWorkflow() {
    return this.getAttribute('transcode-workflow') || 'video_transcode'
  }

  setStatus(text, isError = false) {
    if (!this._statusEl) return
    this._statusEl.textContent = text || ''
    this._statusEl.style.color = isError ? '#b3261e' : '#5c6370'
  }

  setUploading(next) {
    this._uploading = !!next
    if (this._triggerBtn) {
      this._triggerBtn.disabled = this._uploading
      this._triggerBtn.textContent = this._uploading ? this.uploadingLabel : this.label
    }
    if (this._fileInput) this._fileInput.disabled = this._uploading
  }

  publish(payload) {
    if (typeof this._publishAction !== 'function') return
    const body = {}
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'key') return
      if (value === undefined || value === null || value === '') return
      body[key] = value
    })
    // One JSON envelope. EventFrame forwards a single string to
    // publishAction; a bare action name is an opaque token and AEM drops it.
    this._publishAction(JSON.stringify({ [this.actionName]: body }))
  }

  maybeEnqueueTranscode(inputKey, inputUrl = '') {
    if (!this.autoTranscode || !this._onWorkflowStart || !inputKey) return
    const input = { input_key: inputKey, purpose: this.purposeVideo }
    if (inputUrl) input.input_url = inputUrl
    if (this._eventId) input.event_id = this._eventId
    Promise.resolve()
      .then(() => this._onWorkflowStart({ workflow: this.transcodeWorkflow, input }))
      .catch(() => {})
  }

  async uploadPhoto(file) {
    if (!this._onUploadRequest || !this._eventId) {
      this.setStatus('Uploader not ready. Try again in a moment.', true)
      return
    }

    this.setUploading(true)
    try {
      const prepared = await prepareEventImageForUpload(file, this.prepareImageOptions)
      const { url, key } = await uploadPresignedAsset({
        onUploadRequest: this._onUploadRequest,
        eventId: this._eventId,
        file: prepared.file,
        purpose: this.purposePhoto
      })
      const dimensions = await measureImageDimensions(prepared.file)
      this.publish({
        kind: 'photo',
        url,
        s3Key: key,
        filename: prepared.displayName || prepared.file?.name || file.name,
        width: dimensions?.width,
        height: dimensions?.height
      })
      this.setStatus('')
    } catch (error) {
      this.setStatus(error?.message || 'Upload failed', true)
    } finally {
      this.setUploading(false)
    }
  }

  async uploadVideo(file, classified) {
    const sizeError = libraryVideoSizeError(file, this.maxUploadMb)
    if (sizeError) {
      this.setStatus(sizeError, true)
      return
    }

    if (!this._onUploadRequest || !this._eventId) {
      this.setStatus('Uploader not ready. Try again in a moment.', true)
      return
    }

    this.setUploading(true)
    try {
      const durationSecs = await probeVideoDuration(file)
      const durationError = libraryDurationError(durationSecs, {
        minSeconds: this.minSeconds,
        maxUploadSeconds: this.maxUploadSeconds
      })
      if (durationError) {
        this.setStatus(durationError, true)
        return
      }

      const uploaded = await uploadLibraryClip({
        onUploadRequest: this._onUploadRequest,
        eventId: this._eventId,
        blob: file,
        type: classified.type,
        ext: classified.ext,
        durationSecs,
        purpose: this.purposeVideo,
        posterPurpose: this.posterPurpose,
        maxUploadMb: this.maxUploadMb,
        onClipUploaded: (key, url) => this.maybeEnqueueTranscode(key, url),
        capturePoster
      })

      this.publish({
        kind: 'video',
        url: uploaded.url,
        s3Key: uploaded.key,
        filename: uploaded.filename,
        durationSecs: uploaded.durationSecs,
        posterUrl: uploaded.posterUrl,
        posterS3Key: uploaded.posterKey
      })
      this.setStatus('')
    } catch (error) {
      this.setStatus(error?.message || 'Upload failed', true)
    } finally {
      this.setUploading(false)
    }
  }

  async handleFile(file) {
    if (!file || this._uploading) return
    const classified = classifyLibraryFile(file, this.accept)
    if (!classified) {
      this.setStatus(this.invalidTypeMessage, true)
      return
    }
    if (classified.kind === 'photo') {
      await this.uploadPhoto(file)
      return
    }
    await this.uploadVideo(file, classified)
  }

  render() {
    if (this._fileInput) return

    const wrapper = document.createElement('div')
    wrapper.className = 'anear-media-upload'

    this._fileInput = document.createElement('input')
    this._fileInput.type = 'file'
    this._fileInput.accept = this.accept
    this._fileInput.className = 'anear-media-upload-file'
    this._fileInput.style.display = 'none'
    this._fileInput.addEventListener('change', (event) => {
      const file = event.target.files && event.target.files[0]
      this._fileInput.value = ''
      this.handleFile(file)
    })

    this._triggerBtn = document.createElement('button')
    this._triggerBtn.type = 'button'
    this._triggerBtn.className = 'anear-media-upload-trigger'
    this._triggerBtn.textContent = this.label
    this._triggerBtn.addEventListener('click', () => this._fileInput?.click())

    this._statusEl = document.createElement('div')
    this._statusEl.className = 'anear-media-upload-status'

    wrapper.appendChild(this._fileInput)
    wrapper.appendChild(this._triggerBtn)
    wrapper.appendChild(this._statusEl)
    this.appendChild(wrapper)
  }
}

if (!customElements.get('anear-media-upload')) {
  customElements.define('anear-media-upload', AnearMediaUpload)
}

export default AnearMediaUpload
